/**
 * Shared layout tokens for analytics drill-down sheets (BottomSheet +
 * MetricDrillModal family). Matches main Analytics page horizontal rhythm
 * (`px: { xs: 2, sm: 3 }` on AnalyticsTwo).
 */
export const DRILLDOWN_SHEET_CONTENT_PX = { xs: 2, sm: 3 };

/** Mobile sheet height: 10% shorter than the prior 88vh cap. */
export const DRILLDOWN_SHEET_MOBILE_HEIGHT =
  'min(79.2vh, calc((100dvh - 40px) * 0.9))';

/** Shared corner radius for analytics drill-down sheets — aligns with StandardDialog (24px). */
export const DRILLDOWN_SHEET_BORDER_RADIUS = { xs: '24px 24px 0 0', sm: '24px' };

/** Mobile-only elevation behind bottom sheets (no dimmed backdrop). */
export const DRILLDOWN_SHEET_MOBILE_SHADOW = '0 -20px 56px rgba(0,0,0,0.42)';

/**
 * Desktop paper elevation — separates rounded corners from the dim backdrop
 * so anti-aliased edges do not read as dark corner spots (avoid sm: 'none').
 */
export const DRILLDOWN_SHEET_DESKTOP_SHADOW =
  '0 32px 80px rgba(0,0,0,0.18), 0 12px 32px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.06)';

/** Paper chrome shared by analytics drill-down dialogs. */
export const DRILLDOWN_SHEET_PAPER_ELEVATION_SX = {
  boxShadow: { xs: DRILLDOWN_SHEET_MOBILE_SHADOW, sm: DRILLDOWN_SHEET_DESKTOP_SHADOW },
  border: { xs: 'none', sm: '1px solid rgba(255,255,255,0.94)' },
};

/** Sticky header fade — ~15% taller than baseline (78% mask, inset 0). */
export const DRILLDOWN_HEADER_FADE_EXTEND_BOTTOM = { xs: -10, sm: -12 };
export const DRILLDOWN_HEADER_FADE_MASK_STRONG =
  'linear-gradient(to bottom, #000 75%, transparent 100%)';

/** Collapsed trigger — same chrome as "See activity" on dashboard cards. */
export const DRILLDOWN_COLLAPSE_TRIGGER_SX = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 1.25,
  py: 1,
  borderRadius: '8px !important',
  border: '1px solid rgba(0,0,0,0.06)',
  bgcolor: 'rgba(0,0,0,0.015)',
  textTransform: 'none !important',
  transition: 'background 160ms ease',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.035)' },
};

/** Matches AnalyticsFilterSheet panel width so header menus align. */
export const ANALYTICS_HEADER_MENU_WIDTH = {
  xs: 'min(300px, calc(100vw - 16px))',
  sm: 320,
};

/** Date-range pill on AnalyticsTwo — reuse for drill-down sort control. */
export const DRILLDOWN_DATE_PILL_BUTTON_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1.5,
  py: 1,
  minHeight: 36,
  borderRadius: '10px',
  bgcolor: 'rgba(0,0,0,0.05)',
  border: 'none',
  color: 'rgba(0,0,0,0.88)',
  fontSize: '0.85rem',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  transition: 'background 160ms ease',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
};

/** Frosted header layers — mirrors AnalyticsTwo sticky page header. */
export const DRILLDOWN_SHEET_HEADER_LAYER_STRONG_SX = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: DRILLDOWN_HEADER_FADE_EXTEND_BOTTOM,
  pointerEvents: 'none',
  zIndex: 0,
  backgroundColor: 'rgba(255,255,255,0.95)',
  backdropFilter: 'saturate(180%) blur(24px)',
  WebkitBackdropFilter: 'saturate(180%) blur(24px)',
  maskImage: DRILLDOWN_HEADER_FADE_MASK_STRONG,
  WebkitMaskImage: DRILLDOWN_HEADER_FADE_MASK_STRONG,
};

export const DRILLDOWN_SHEET_HEADER_LAYER_SOFT_SX = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: DRILLDOWN_HEADER_FADE_EXTEND_BOTTOM,
  pointerEvents: 'none',
  zIndex: 0,
  backgroundColor: 'rgba(255,255,255,0)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  maskImage: 'linear-gradient(to bottom, #000, transparent)',
  WebkitMaskImage: 'linear-gradient(to bottom, #000, transparent)',
};

export const DRILLDOWN_SHEET_HEADER_SX = {
  position: 'relative',
  zIndex: 2,
  px: DRILLDOWN_SHEET_CONTENT_PX,
  pt: DRILLDOWN_SHEET_CONTENT_PX,
  pb: { xs: 1, sm: 1.25 },
  flexShrink: 0,
  minHeight: { xs: 48, sm: 52 },
  overflow: 'visible',
};

/** Sheet header date-range line (e.g. "Last 7 days"). */
export const DRILLDOWN_SHEET_HEADER_SUBTITLE_SX = {
  display: 'block',
  color: 'rgba(0,0,0,0.4)',
  fontSize: '0.68rem',
  fontWeight: 400,
  mt: 0.15,
  lineHeight: 1.35,
};

/** Scrollport for sheet body only — header stays fixed above the scrollbar. */
export const DRILLDOWN_SHEET_BODY_SCROLL_SX = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
};

/** @deprecated Use DRILLDOWN_SHEET_BODY_SCROLL_SX — body-only scrollport. */
export const DRILLDOWN_SHEET_SCROLL_SX = DRILLDOWN_SHEET_BODY_SCROLL_SX;

export const DRILLDOWN_SHEET_BODY_SX = {
  px: DRILLDOWN_SHEET_CONTENT_PX,
  py: { xs: 2.5, sm: 3 },
  paddingBottom: {
    xs: 'calc(env(safe-area-inset-bottom) + 24px)',
    sm: 3,
  },
};

/** Paper chrome for listing-management dialogs — matches BottomSheet on mobile. */
export const ANALYTICS_LISTING_MODAL_PAPER_SX = {
  borderRadius: DRILLDOWN_SHEET_BORDER_RADIUS,
  ...DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
  m: { xs: 0, sm: 2 },
  mt: { xs: 'auto', sm: 2 },
  width: { xs: '100%', sm: 'auto' },
  maxWidth: { xs: '100%', sm: undefined },
};

/**
 * Dialog shell for listing link / unlink / URL modals.
 * Mobile: full-width bottom sheet, no dimmed backdrop.
 * Desktop: centered card with standard analytics backdrop.
 */
export const getAnalyticsListingModalDialogProps = (isMobile) => ({
  hideBackdrop: isMobile,
  slotProps: {
    backdrop: isMobile ? undefined : { sx: { bgcolor: 'rgba(0,0,0,0.32)' } },
  },
  PaperProps: { sx: ANALYTICS_LISTING_MODAL_PAPER_SX },
});
